import {
  Flex,
  Text,
  Image,
  CallVideoIcon,
  VolumeUpIcon,
  MicIcon,
  Button,
  ButtonContent,
  ChevronEndIcon,
  ErrorIcon,
  ExclamationTriangleIcon,
  AcceptIcon,
  WindowMinimizeIcon,
} from "@fluentui/react-northstar";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFluentColorScheme } from "../../../../shared/theme/useColorScheme";
import { Devices } from "../../../../types/Device.types";
import { DeviceStatus } from "../../results-view/ResultsView.types";
import { useDeviceTesterDispatchContext } from "../../../device-tester-context-provider/DeviceTesterContextProvider";
import { DeviceTesterActionType } from "../../../device-tester-context-provider/Reducer.types";
import { deviceToDialogName } from "../../results-device-status-dialog-box/DeviceStatusDialogBox.types";

import { useResultsDesktopDeviceStatusCardStyles } from "./DesktopDeviceStatusCard.styles";
import { ResultsDesktopDeviceStatusCardProps } from "./DesktopDeviceStatusCard.types";

// TODO: refac as HoC
export const ResultsDesktopDeviceStatusCard: React.FC<
  ResultsDesktopDeviceStatusCardProps
> = ({ testResult, deviceName, selectedDevice, isInfoFetched }) => {
  const desktopDeviceTitleStyles = useResultsDesktopDeviceStatusCardStyles();
  const [hoverIcon, setHoverIcon] = useState<any>(undefined);
  const { deviceTesterDispatch } = useDeviceTesterDispatchContext();
  const { main, silver } = useFluentColorScheme();
  const { t } = useTranslation();

  const networkConnectedString = t("NETWORK_CONNECTED");
  const networkDisconnectedString = t("NETWORK_DISCONNECTED");

  const openDialog = useCallback(() => {
    deviceTesterDispatch({
      type: DeviceTesterActionType.SET_DEVICE_STATUS_DIALOG_STATE,
      deviceStatusDialogState: deviceToDialogName[deviceName],
    });
  }, [deviceTesterDispatch, deviceName]);

  const displayIcon = () => {
    setHoverIcon(<ChevronEndIcon size="large" />);
  };

  const hideIcon = () => {
    setHoverIcon(undefined);
  };

  const deviceWorkingAriaLabel = useMemo(() => {
    switch (deviceName) {
      case Devices.Network:
        return t("NETWORK_CONNECTED_A11Y_LABEL");
      case Devices.Camera:
        return t("CAMERA_WORKING_A11Y_LABEL");
      case Devices.Speaker:
        return t("SPEAKER_WORKING_A11Y_LABEL");
      case Devices.Mic:
        return t("MICROPHONE_WORKING_A11Y_LABEL");
    }
  }, [deviceName, t]);

  // TODO: fix to use titlecase fn
  const deviceNameString = useMemo(() => {
    switch (deviceName) {
      case Devices.Network:
        return t("NETWORK_TITLECASE");
      case Devices.Camera:
        return t("CAMERA_TITLECASE");
      case Devices.Speaker:
        return t("SPEAKER_TITLECASE");
      case Devices.Mic:
        return t("MIC_TITLECASE");
    }
  }, [deviceName, t]);

  // Todo add cache key to memo otherwise re-render every time
  const getIcon = useMemo(() => {
    switch (deviceName) {
      case Devices.Network:
        return (
          <Image
            styles={desktopDeviceTitleStyles.deviceIcon}
            src="/assets/images/connectionIconWhite.svg"
          />
        );
      case Devices.Camera:
        return (
          <CallVideoIcon variables={{ color: "white" }} outline size="medium" />
        );
      case Devices.Speaker:
        return (
          <VolumeUpIcon variables={{ color: "white" }} outline size="medium" />
        );
      case Devices.Mic:
        return <MicIcon variables={{ color: "white" }} outline size="medium" />;
    }
  }, [deviceName, desktopDeviceTitleStyles.deviceIcon]);

  const getDesktopLabel = useMemo(() => {
    if (deviceName === Devices.Network && isInfoFetched) {
      return testResult.status === DeviceStatus.WORKING
        ? networkConnectedString
        : networkDisconnectedString;
    } else if (selectedDevice) {
      return selectedDevice.name;
    } else {
      return undefined;
    }
  }, [
    selectedDevice,
    deviceName,
    testResult.status,
    isInfoFetched,
    networkConnectedString,
    networkDisconnectedString,
  ]);

  return (
    <Button
      styles={desktopDeviceTitleStyles.deviceCard}
      onClick={openDialog}
      disabled={!isInfoFetched}
      fluid
      onMouseEnter={displayIcon}
      onMouseLeave={hideIcon}
      icon={hoverIcon}
      iconPosition="after"
      variables={{
        backgroundColorHover: silver.backgroundHover,
        borderColor: "transparent",
        borderColorHover: "transparent",
        backgroundColorActive: main.backgroundFocus2,
      }}
      content={
        <ButtonContent>
          <Flex styles={{ width: "430px", height: "85px" }} hAlign="stretch">
            <Flex
              styles={desktopDeviceTitleStyles.deviceTitleContainer}
              column
              gap="gap.small"
              vAlign="start"
              hAlign="start"
              padding="padding.medium"
            >
              <Flex gap="gap.small" vAlign="center">
                <Flex hAlign="start" vAlign="center">
                  {getIcon}
                </Flex>
                <Text
                  styles={desktopDeviceTitleStyles.deviceTypeName}
                  size="large"
                  content={deviceNameString}
                />
              </Flex>

              <Text
                truncated
                styles={desktopDeviceTitleStyles.selectedDevice}
                content={<>{getDesktopLabel}</>}
                size="medium"
              />
            </Flex>
            <Flex vAlign="center" hAlign="end">
              {testResult.status === DeviceStatus.FAILED && (
                <Flex vAlign="center" hAlign="center">
                  <ErrorIcon size="large" />
                </Flex>
              )}
              {testResult.status === DeviceStatus.MISSING && (
                <Flex vAlign="center" hAlign="center">
                  <ErrorIcon size="large" />
                </Flex>
              )}
              {testResult.status === DeviceStatus.UNAVAILABLE && (
                <ExclamationTriangleIcon
                  size="large"
                  styles={desktopDeviceTitleStyles.warningIcon}
                />
              )}
              {testResult.status === DeviceStatus.WORKING && (
                <Flex vAlign="center" hAlign="center">
                  <AcceptIcon
                    aria-label={deviceWorkingAriaLabel}
                    bordered
                    circular
                    size="small"
                    styles={desktopDeviceTitleStyles.successIcon}
                  />
                </Flex>
              )}
              {testResult.status === DeviceStatus.UNTESTED && (
                <Flex vAlign="center" hAlign="center">
                  <WindowMinimizeIcon size="smaller" color="gray" />
                </Flex>
              )}
            </Flex>
          </Flex>
        </ButtonContent>
      }
    />
  );
};
